import { useEffect, useRef, useState } from 'react';
import {
  ThunderboltOutlined,
  PlusOutlined,
  FileTextOutlined,
  LoadingOutlined,
  SendOutlined,
  DownOutlined,
  DeleteOutlined,
  EditOutlined,
  CopyOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import { Modal } from 'antd';
import dayjs from 'dayjs';
import { NotificationAlert } from '../../../components';
import { Statuses } from '../../../helper';
import { getAllUsers } from '../../../services/userServices';
import { getAllObjectives } from '../../../services/objectiveServices';
import { getAllQuarters, updateFeedbacks } from '../../../services/reviewsServices';
import {
  createFeedbackNote,
  deleteFeedbackNote,
  updateFeedbackNote,
  generateFeedbackSummary,
  getFeedbackNotes,
} from '../../../services/notesService';
import {
  AllReviewsProps,
  FeedbackNote,
  MenteesProps,
  menteeObjectiveProps,
  objectiveFeedback,
} from '../../../types';

const C = {
  navy: '#12284b',
  cyan: '#1aa3e8',
  blue: '#2563eb',
  blueSoft: '#e8f0fe',
  green: '#16a34a',
  greenSoft: '#dcfce7',
  amber: '#d97706',
  amberSoft: '#fef3c7',
  gray: '#6b7280',
  line: '#e5e7eb',
};

const GENERAL = 'general';
const ALL_QUARTERS = 'all';

type BoxStatus = 'empty' | 'draft' | 'submitted';
const BOX_BADGE: Record<BoxStatus, { label: string; bg: string; color: string }> = {
  empty: { label: 'Feedback box: empty', bg: '#f1f5f9', color: C.gray },
  draft: { label: 'Feedback box: draft', bg: C.amberSoft, color: C.amber },
  submitted: { label: 'Feedback box: submitted ✓', bg: C.greenSoft, color: C.green },
};

// quarter names sometimes already include the year — avoid "Q2 2037 2037"
const quarterLabel = (q: AllReviewsProps) =>
  q.name.includes(String(q.year)) ? q.name : `${q.name} ${q.year}`;

// "21st July 2026" — day with an ordinal suffix, spelled-out month and year
const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
const generatedStamp = (d: dayjs.Dayjs) =>
  `AI summary generated on ${ordinal(d.date())} ${d.format('MMMM YYYY')}`;

// Mentor view: log notes about a mentee per objective, pick the quarter you're working in,
// summarize an objective, and send the draft to that objective's Mentor Feedback box.
const FeedbackNotes = () => {
  const myRef = useRef<any>();
  const [mentees, setMentees] = useState<MenteesProps[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [quarters, setQuarters] = useState<AllReviewsProps[]>([]);
  const [quarterSel, setQuarterSel] = useState<string>(ALL_QUARTERS);
  // year drives which quarters appear in the Quarter dropdown; 'all' = every quarter
  const [yearSel, setYearSel] = useState<string>(ALL_QUARTERS);
  const [objectives, setObjectives] = useState<menteeObjectiveProps[]>([]);
  const [notes, setNotes] = useState<FeedbackNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);

  const [draft, setDraft] = useState('');
  // the objective currently in focus: a concrete objective id, or GENERAL
  const [objSel, setObjSel] = useState<string>(GENERAL);
  const [summary, setSummary] = useState('');
  const [generatedAt, setGeneratedAt] = useState<dayjs.Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);
  // edit-note modal
  const [editNote, setEditNote] = useState<FeedbackNote | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const active = mentees.find((m) => m.id === activeId);
  const inProgressIds = new Set(quarters.filter((q) => q.status === 1).map((q) => q.id));
  const selectedQuarter = quarters.find((q) => q.id === quarterSel);

  // the single quarter dropdown is split into Year + Quarter
  const years = [...new Set(quarters.map((q) => String(q.year)))].sort(
    (a, b) => Number(b) - Number(a)
  );
  const quartersForYear = quarters.filter((q) => String(q.year) === yearSel);
  // the quarter name sometimes already includes the year — strip it so the option reads "Q3"
  const quarterOnlyLabel = (q: AllReviewsProps) =>
    q.name.replace(String(q.year), '').trim() || q.name;
  const onYearChange = (val: string) => {
    setYearSel(val);
    if (val === ALL_QUARTERS) return setQuarterSel(ALL_QUARTERS);
    const inYear = quarters.filter((q) => String(q.year) === val);
    const current = inYear.find((q) => q.status === 1) || inYear[0];
    setQuarterSel(current ? current.id : ALL_QUARTERS);
  };

  // all the mentee's notes are fetched once; the quarter filter is applied client-side so
  // switching quarters is instant (no refetch).
  const scopedNotes =
    quarterSel === ALL_QUARTERS ? notes : notes.filter((n) => n.quarterId === quarterSel);
  const notesForObjective = (objId: string) =>
    scopedNotes.filter((n) => n.objectiveId === objId).length;
  const generalNotesCount = scopedNotes.filter((n) => !n.objectiveId).length;

  const isConcreteObj = objSel !== GENERAL && objSel !== '';

  // notes shown, scoped to the objective in focus
  const filteredNotes = isConcreteObj
    ? scopedNotes.filter((n) => n.objectiveId === objSel)
    : scopedNotes.filter((n) => !n.objectiveId);

  const changeObj = (value: string) => {
    setObjSel(value);
    setSummary('');
    setGeneratedAt(null);
    setSent(false);
  };

  // the feedback record the draft would be saved into: the selected quarter's (if it is
  // in progress), or any in-progress quarter when viewing all quarters
  const targetComment = (obj?: menteeObjectiveProps): objectiveFeedback | undefined => {
    const comments = obj?.objectiveComments || [];
    if (quarterSel !== ALL_QUARTERS) {
      const c = comments.find((x) => x.quarterId === quarterSel);
      return c && inProgressIds.has(c.quarterId) ? c : undefined;
    }
    return comments.find((c) => !!c.quarterId && inProgressIds.has(c.quarterId));
  };

  // the per-objective Mentor Feedback box state, for the card badge
  const feedbackBoxStatus = (obj: menteeObjectiveProps): BoxStatus => {
    const comments = obj.objectiveComments || [];
    const c =
      quarterSel !== ALL_QUARTERS
        ? comments.find((x) => x.quarterId === quarterSel)
        : comments.find((x) => inProgressIds.has(x.quarterId)) || comments[0];
    const status = (c?.mentorCommentStatus || '').toUpperCase();
    if (status === 'COMPLETED' || status === 'SUBMITTED_TO_HR') return 'submitted';
    if (c?.mentorComment && c.mentorComment.trim()) return 'draft';
    return 'empty';
  };

  const selectedObj = isConcreteObj ? objectives.find((o) => o.id === objSel) : undefined;
  const sendTarget = targetComment(selectedObj);
  const focusNoteCount = isConcreteObj ? notesForObjective(objSel) : generalNotesCount;

  // load mentees + quarters once; default the dropdown to the in-progress quarter
  useEffect(() => {
    (async () => {
      const [{ data: menteeData, error }, { data: quarterData }] = await Promise.all([
        getAllUsers('mentor', true), // notes page shows names only — skip the Graph photo prefetch
        getAllQuarters('mentor'),
      ]);
      if (error) return myRef.current?.call('error', error?.message);
      setMentees(menteeData);
      if (menteeData[0]) setActiveId(menteeData[0].id);
      setQuarters(quarterData);
      const current = quarterData.find((q: AllReviewsProps) => q.status === 1);
      if (current) {
        setQuarterSel(current.id);
        setYearSel(String(current.year));
      }
    })();
  }, []);

  // load the selected mentee's objectives; focus the first objective
  useEffect(() => {
    if (!activeId) return;
    (async () => {
      const { data, error } = await getAllObjectives(activeId, 'mentor');
      if (error) return myRef.current?.call('error', error?.message);
      setObjectives(data);
      setObjSel(data[0]?.id || GENERAL);
      setSummary('');
      setGeneratedAt(null);
      setSent(false);
    })();
  }, [activeId]);

  // load ALL of the selected mentee's notes once (quarter filtering is client-side)
  useEffect(() => {
    if (!activeId) return;
    setNotesLoading(true);
    (async () => {
      const { data, error } = await getFeedbackNotes(activeId);
      setNotesLoading(false);
      if (error) return myRef.current?.call('error', error?.message);
      setNotes(data);
    })();
  }, [activeId]);

  // changing the quarter only re-scopes locally, so just invalidate the stale summary
  useEffect(() => {
    setSummary('');
    setGeneratedAt(null);
    setSent(false);
  }, [quarterSel]);

  const refreshNotes = async () => {
    const { data, error } = await getFeedbackNotes(activeId);
    if (error) return myRef.current?.call('error', error?.message);
    setNotes(data);
  };

  const addNote = async () => {
    if (!draft.trim() || !activeId) return;
    const noteObj = isConcreteObj ? objectives.find((o) => o.id === objSel) : undefined;
    setAdding(true);
    const { error } = await createFeedbackNote({
      menteeId: activeId,
      objectiveId: isConcreteObj ? objSel : null,
      quarterId:
        quarterSel !== ALL_QUARTERS ? quarterSel : targetComment(noteObj)?.quarterId ?? null,
      noteText: draft.trim(),
    });
    setAdding(false);
    if (error) return myRef.current?.call('error', error?.message);
    setDraft('');
    setSummary(''); // notes changed → summary stale
    setGeneratedAt(null);
    setSent(false);
    refreshNotes();
  };

  const removeNote = async (id: string) => {
    const { error } = await deleteFeedbackNote(id);
    if (error) return myRef.current?.call('error', error?.message);
    setSummary('');
    setGeneratedAt(null);
    setSent(false);
    refreshNotes();
  };

  // confirm before deleting — deletion is permanent
  const confirmRemoveNote = (n: FeedbackNote) => {
    const preview = n.noteText.length > 90 ? `${n.noteText.slice(0, 90)}…` : n.noteText;
    Modal.confirm({
      title: 'Delete this note?',
      icon: <ExclamationCircleFilled />,
      content: `This permanently deletes the note: "${preview}". This can't be undone.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => removeNote(n.id),
    });
  };

  const openEdit = (n: FeedbackNote) => {
    setEditNote(n);
    setEditText(n.noteText);
  };

  const saveEdit = async () => {
    if (!editNote || !editText.trim()) return;
    setSavingEdit(true);
    const { error } = await updateFeedbackNote(editNote.id, editText.trim());
    setSavingEdit(false);
    if (error) return myRef.current?.call('error', error?.message);
    setEditNote(null);
    setSummary(''); // notes changed → summary stale
    setGeneratedAt(null);
    setSent(false);
    refreshNotes();
  };

  const generate = async () => {
    if (!isConcreteObj || !activeId) return;
    setLoading(true);
    setSummary('');
    setGeneratedAt(null);
    setSent(false);
    const { data, error } = await generateFeedbackSummary(
      activeId,
      quarterSel === ALL_QUARTERS ? undefined : quarterSel,
      objSel
    );
    setLoading(false);
    if (error) return myRef.current?.call('error', error?.message);
    setSummary(data || '');
    if (data) setGeneratedAt(dayjs());
  };

  // save the draft into the selected objective's Mentor Feedback box
  const sendToFeedback = async () => {
    if (!summary || !selectedObj || !sendTarget) return;
    const updatedObj: menteeObjectiveProps = {
      ...selectedObj,
      objectiveComments: (selectedObj.objectiveComments || []).map((c) =>
        c.id === sendTarget.id ? { ...c, mentorComment: summary } : c
      ),
    };
    setSending(true);
    const { error } = await updateFeedbacks({
      quarterlyObjectivesList: [updatedObj],
      commentStatus: Statuses.InProgress,
      quarterId: sendTarget.quarterId,
    });
    setSending(false);
    if (error) return myRef.current?.call('error', error?.message);
    setSent(true);
    myRef.current?.call('success', 'Draft saved successfully');
  };

  // confirm the AI summary is good before saving it as a draft
  const confirmSendToFeedback = () => {
    if (!summary || !selectedObj || !sendTarget) return;
    Modal.confirm({
      title: 'Save this summary as a draft?',
      icon: <ExclamationCircleFilled />,
      content:
        'Is the summarized note good to be saved as a draft in this objective’s Quarterly Feedback?',
      okText: 'Yes, save draft',
      cancelText: 'Cancel',
      onOk: () => sendToFeedback(),
    });
  };

  const selectCls =
    'appearance-none rounded-full border bg-white py-2 pl-4 pr-9 text-sm font-medium shadow-sm outline-none transition-shadow focus:ring-2 focus:ring-blue-100';

  return (
    <div
      className="mx-auto flex max-w-[1500px] flex-col gap-y-4 rounded-2xl border px-8 py-6"
      style={{ borderColor: C.line }}
    >
      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-[24px] font-semibold" style={{ color: C.navy }}>
              Feedback Notes
            </div>
            <span
              className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
              style={{ background: '#e6f6fd', color: C.cyan }}
            >
              About your mentees
            </span>
          </div>
          <div className="mt-1 max-w-xl text-sm" style={{ color: C.gray }}>
            Capture observations about your mentees through the quarter, then turn each objective’s
            notes into a quarterly feedback draft. <b style={{ color: C.navy }}>Private to you.</b>
          </div>
        </div>

        {/* right controls: mentee + quarter */}
        <div className="flex items-end gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium" style={{ color: C.gray }}>
            Notes for
            <div className="relative">
              <select
                value={activeId}
                onChange={(e) => setActiveId(e.target.value)}
                className={selectCls}
                style={{ borderColor: C.line, color: C.navy, minWidth: 200 }}
              >
                {mentees.length === 0 && <option value="">No mentees assigned</option>}
                {mentees.map((mn) => (
                  <option key={mn.id} value={mn.id}>
                    {mn.name}
                  </option>
                ))}
              </select>
              <DownOutlined
                style={{ color: C.gray, fontSize: 12 }}
                className="pointer-events-none absolute right-3.5 top-3"
              />
            </div>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium" style={{ color: C.gray }}>
            Year
            <div className="relative">
              <select
                value={yearSel}
                onChange={(e) => onYearChange(e.target.value)}
                className={selectCls}
                style={{ borderColor: C.line, color: C.navy, minWidth: 120 }}
              >
                <option value={ALL_QUARTERS}>All years</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <DownOutlined
                style={{ color: C.gray, fontSize: 12 }}
                className="pointer-events-none absolute right-3.5 top-3"
              />
            </div>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium" style={{ color: C.gray }}>
            Quarter
            <div className="relative">
              <select
                value={quarterSel}
                onChange={(e) => setQuarterSel(e.target.value)}
                disabled={yearSel === ALL_QUARTERS}
                className={selectCls}
                style={{
                  borderColor: C.line,
                  color: C.navy,
                  minWidth: 140,
                  opacity: yearSel === ALL_QUARTERS ? 0.55 : 1,
                }}
              >
                {yearSel === ALL_QUARTERS ? (
                  <option value={ALL_QUARTERS}>All quarters</option>
                ) : (
                  quartersForYear.map((q) => (
                    <option key={q.id} value={q.id}>
                      {quarterOnlyLabel(q)}
                      {q.status === 1 ? ' · current' : ''}
                    </option>
                  ))
                )}
              </select>
              <DownOutlined
                style={{ color: C.gray, fontSize: 12 }}
                className="pointer-events-none absolute right-3.5 top-3"
              />
            </div>
          </label>
        </div>
      </div>

      {/* body: objective cards (left) + notes & AI (right) */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px_1fr]">
        {/* objective cards */}
        <div>
          <div
            className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide"
            style={{ color: C.gray }}
          >
            Objectives {selectedQuarter ? `— ${quarterLabel(selectedQuarter)}` : ''}
          </div>
          <div
            className="rounded-2xl border p-2.5"
            style={{ borderColor: C.line, background: '#fafbfd' }}
          >
            <div
              className="flex flex-col gap-2 overflow-y-auto pr-1"
              style={{ maxHeight: 480 }}
            >
              {/* general (no objective) — always first in the list */}
              <button
                onClick={() => changeObj(GENERAL)}
                className="rounded-2xl border bg-white p-3.5 text-left transition-shadow hover:shadow-sm"
                style={{
                  borderColor: objSel === GENERAL ? C.navy : C.line,
                  boxShadow: objSel === GENERAL ? `0 0 0 1px ${C.navy}` : undefined,
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: C.gray }}
                  >
                    General
                  </span>
                  <span className="text-[11px]" style={{ color: C.gray }}>
                    {generalNotesCount} note{generalNotesCount === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="mt-1 text-sm font-medium leading-snug" style={{ color: C.navy }}>
                  General notes (no objective)
                </div>
              </button>

              {objectives.map((o) => {
                const selected = objSel === o.id;
                const box = BOX_BADGE[feedbackBoxStatus(o)];
                const n = notesForObjective(o.id);
                return (
                  <button
                    key={o.id}
                    onClick={() => changeObj(o.id)}
                    className="rounded-2xl border bg-white p-3.5 text-left transition-shadow hover:shadow-sm"
                    style={{
                      borderColor: selected ? C.navy : C.line,
                      boxShadow: selected ? `0 0 0 1px ${C.navy}` : undefined,
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide"
                        style={{ color: C.blue }}
                      >
                        {o.objectiveCategory}
                      </span>
                      <span className="text-[11px]" style={{ color: C.gray }}>
                        {n} note{n === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="mt-1 text-sm font-medium leading-snug" style={{ color: C.navy }}>
                      {o.description}
                    </div>
                    <span
                      className="mt-2 inline-block rounded-full px-2 py-0.5 text-[10.5px] font-semibold"
                      style={{ background: box.bg, color: box.color }}
                    >
                      {box.label}
                    </span>
                  </button>
                );
              })}

              {objectives.length === 0 && (
                <div className="py-6 text-center text-sm" style={{ color: C.gray }}>
                  {active ? `No objectives for ${active.name}.` : 'Select a mentee.'}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 px-1 text-xs leading-relaxed" style={{ color: C.gray }}>
            Notes and summaries are scoped to one objective — matching how Quarterly Feedback is
            stored (one feedback box per objective).
          </div>
        </div>

        {/* notes + AI */}
        <div className="flex flex-col gap-4">
          {/* notes card */}
          <div
            className="rounded-2xl border bg-white p-5 shadow-sm"
            style={{ borderColor: C.line }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[17px] font-semibold" style={{ color: C.navy }}>
                  {isConcreteObj ? selectedObj?.description : 'General notes (no objective)'}
                </div>
                <div className="mt-0.5 text-xs" style={{ color: C.gray }}>
                  {isConcreteObj ? `${selectedObj?.objectiveCategory} · ` : ''}notes are kept per
                  objective
                </div>
              </div>
              <span
                className="shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                style={{ background: C.blueSoft, color: C.blue }}
              >
                {filteredNotes.length} note{filteredNotes.length === 1 ? '' : 's'} this quarter
              </span>
            </div>

            {/* notes timeline */}
            <div className="mt-4 overflow-y-auto pr-1" style={{ maxHeight: 260 }}>
              {filteredNotes.map((n, i) => (
                <div
                  key={n.id}
                  className="flex gap-4 py-3"
                  style={{ borderTop: i === 0 ? 'none' : `1px solid #f1f5f9` }}
                >
                  <div className="w-16 shrink-0">
                    <div className="text-xs font-semibold" style={{ color: C.navy }}>
                      {dayjs(n.createdDate).format('MMM DD')}
                    </div>
                    <div className="text-[10px]" style={{ color: C.gray }}>
                      {dayjs(n.createdDate).format('YYYY')}
                    </div>
                  </div>
                  <div className="flex-1 text-sm leading-relaxed" style={{ color: '#374151' }}>
                    {n.noteText}
                  </div>
                  <div className="flex shrink-0 items-start gap-3">
                    <button
                      onClick={() => openEdit(n)}
                      title="Edit note"
                      className="transition-colors hover:opacity-70"
                      style={{ color: C.blue, fontSize: 13 }}
                    >
                      <EditOutlined />
                    </button>
                    <button
                      onClick={() => confirmRemoveNote(n)}
                      title="Delete note"
                      className="transition-colors hover:opacity-70"
                      style={{ color: '#b91c1c', fontSize: 13 }}
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                </div>
              ))}
              {notesLoading && filteredNotes.length === 0 && (
                <div className="space-y-2 py-2">
                  {[100, 85, 70].map((w) => (
                    <div
                      key={w}
                      className="h-4 animate-pulse rounded"
                      style={{ width: `${w}%`, background: '#eef2f7' }}
                    />
                  ))}
                </div>
              )}
              {!notesLoading && filteredNotes.length === 0 && (
                <div
                  className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-8 text-center text-sm"
                  style={{ borderColor: C.line, color: C.gray }}
                >
                  <FileTextOutlined style={{ color: '#cbd5e1', fontSize: 24 }} />
                  No notes for this objective yet. Add your first meeting note below.
                </div>
              )}
            </div>

            {/* add note */}
            <div className="mt-3 flex items-end gap-2 border-t pt-3" style={{ borderColor: '#f1f5f9' }}>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={2}
                placeholder="Add a note from a meeting, 1:1, observation…"
                className="flex-1 resize-none rounded-xl border p-2.5 text-sm outline-none transition-shadow focus:ring-2 focus:ring-blue-100"
                style={{ borderColor: C.line }}
              />
              <button
                onClick={addNote}
                disabled={adding || !draft.trim() || !activeId}
                className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: C.navy }}
              >
                {adding ? <LoadingOutlined /> : <PlusOutlined />} Add note
              </button>
            </div>
          </div>

          {/* AI summary (below) */}
          <div
            className="rounded-2xl border p-5 shadow-sm"
            style={{ background: '#eef4fe', borderColor: '#d3e2fb' }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: C.navy }}>
                  <ThunderboltOutlined style={{ color: C.blue }} /> AI summary
                  <span className="text-xs font-normal" style={{ color: C.gray }}>
                    — this objective only
                  </span>
                </div>
                <div className="mt-1 text-xs" style={{ color: C.gray }}>
                  Summarizes the {focusNoteCount} note{focusNoteCount === 1 ? '' : 's'} on this
                  objective into a Mentor Feedback draft.
                </div>
                {generatedAt && (
                  <span
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium"
                    style={{ borderColor: '#cddffb', background: '#fff', color: C.navy }}
                  >
                    <ClockCircleOutlined style={{ color: C.blue }} /> {generatedStamp(generatedAt)}
                  </span>
                )}
              </div>
              <button
                onClick={generate}
                disabled={loading || !isConcreteObj || focusNoteCount === 0}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-40"
                style={{ background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})` }}
              >
                {loading ? <LoadingOutlined /> : <ThunderboltOutlined />}
                {loading ? 'Generating…' : summary ? 'Regenerate' : 'Generate summary'}
              </button>
            </div>

            {!summary && !loading && (
              <div
                className="mt-4 rounded-xl border border-dashed px-4 py-5 text-sm leading-relaxed"
                style={{ borderColor: '#c7d7f5', color: C.gray }}
              >
                {!isConcreteObj
                  ? 'Pick an objective card on the left to summarize its notes.'
                  : focusNoteCount > 0
                    ? `The summary will cover only "${selectedObj?.description}" — one objective usually collects notes from several meetings across the quarter, and Quarterly Feedback is stored per objective, so the draft maps 1-to-1 onto its feedback box.`
                    : 'No notes for this objective yet — add some above.'}
              </div>
            )}
            {loading && (
              <div className="mt-4 space-y-2 py-2">
                {[100, 92, 78, 60].map((w) => (
                  <div
                    key={w}
                    className="h-3 animate-pulse rounded-full"
                    style={{ width: `${w}%`, background: C.blueSoft }}
                  />
                ))}
                <p className="pt-2 text-sm" style={{ color: C.gray }}>
                  Reading this objective's notes…
                </p>
              </div>
            )}
            {summary && !loading && (
              <div className="mt-4">
                <textarea
                  value={summary}
                  onChange={(e) => {
                    setSummary(e.target.value);
                    setSent(false);
                  }}
                  rows={8}
                  className="w-full resize-none rounded-xl border p-3 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-blue-100"
                  style={{ borderColor: C.line, color: '#1f2937', background: '#fff' }}
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={confirmSendToFeedback}
                    disabled={sending || !sendTarget}
                    title={
                      sendTarget
                        ? 'Save this draft to the objective’s Mentor Feedback box'
                        : 'This objective has no feedback box in an active quarter'
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ background: C.green }}
                  >
                    {sending ? <LoadingOutlined /> : <SendOutlined />} Send to Quarterly Feedback
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(summary);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-4 py-2 text-sm font-medium transition-colors hover:bg-slate-50"
                    style={{ borderColor: C.line, color: C.navy }}
                  >
                    {copied ? <CheckOutlined style={{ color: C.green }} /> : <CopyOutlined />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                {sent && (
                  <div
                    className="mt-2 rounded-xl px-3 py-2 text-xs leading-relaxed"
                    style={{ background: C.greenSoft, color: C.green }}
                  >
                    Saved as a draft in this objective's Quarterly Feedback. Review and submit it on
                    the Quarterly Feedback page — nothing was submitted automatically.
                  </div>
                )}
                {!sendTarget && (
                  <div
                    className="mt-2 rounded-xl px-3 py-2 text-xs leading-relaxed"
                    style={{ background: C.amberSoft, color: C.amber }}
                  >
                    {quarterSel !== ALL_QUARTERS && selectedQuarter?.status !== 1
                      ? `${selectedQuarter ? quarterLabel(selectedQuarter) : 'This quarter'} isn't in progress, so the draft can't be sent to Quarterly Feedback — you can still copy it.`
                      : "This objective isn't in an active quarterly cycle, so it can't be sent to Quarterly Feedback — you can still copy it."}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* edit-note modal */}
      <Modal
        open={!!editNote}
        title="Edit note"
        okText="Save"
        cancelText="Discard"
        onOk={saveEdit}
        onCancel={() => setEditNote(null)}
        confirmLoading={savingEdit}
        okButtonProps={{ disabled: !editText.trim() }}
        centered
      >
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          rows={5}
          placeholder="Edit your note…"
          className="w-full resize-none rounded-lg border p-3 text-sm outline-none focus:ring-2 focus:ring-blue-100"
          style={{ borderColor: C.line }}
        />
      </Modal>

      <NotificationAlert ref={myRef} />
    </div>
  );
};

export default FeedbackNotes;
